import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, forwardRef, Input, Output } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DATE_OF_BIRTH_PICKER_MAX_AGE_YEARS } from 'src/app/v2/components/public/waiver-builder/waiver-compliance';

/**
 * Month / Day / Year dropdowns for entering a date of birth.
 *
 * Replaces `<input type="date">`, which is a poor fit for a birthday: its
 * calendar opens on the CURRENT month with no way to jump, so reaching a
 * birth year means dozens of interactions. Bounding it with `min`/`max`
 * helped but didn't change that. Three selects remove the navigation
 * entirely — the year list is a native dropdown, so typing "1985" lands on
 * it immediately, and on mobile each field is a short native wheel.
 *
 * It also removes the MM/DD vs DD/MM ambiguity a single text field has,
 * because each part is named.
 *
 * Value is the same calendar string the rest of the waiver code uses —
 * `YYYY-MM-DD`, never a `Date` — so nothing downstream changes. It stays
 * empty until all three parts are chosen: no partial date is ever
 * persisted. Hosts that accept a blank date of birth read
 * {@link DateOfBirthPickerComponent.partialChange} to tell a half-finished
 * entry apart from a deliberately empty one.
 *
 * Deliberately NOT validating here: `validateDateOfBirth` in
 * `waiver-compliance.ts` remains the single source of truth for whether an
 * entry is usable (missing / malformed / future), and each host already
 * renders its message. This control only guarantees the parts it offers
 * form a real calendar date — impossible days (31 Feb) are never listed.
 */
@Component({
  selector: 'app-date-of-birth-picker',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './date-of-birth-picker.component.html',
  styleUrls: ['./date-of-birth-picker.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => DateOfBirthPickerComponent),
    multi: true,
  }],
})
export class DateOfBirthPickerComponent implements ControlValueAccessor {

  constructor(private host: ElementRef<HTMLElement>) {}

  /** Prefix for the generated element ids, so several pickers can coexist. */
  @Input() idPrefix = 'dob';
  /** Applies the invalid styling; the host owns the message itself. */
  @Input() invalid = false;
  /** Compact sizing to match `form-control-sm` hosts. */
  @Input() small = false;

  /**
   * Emits when the control crosses between partially-filled and not.
   *
   * The model value cannot express this: it is `''` both when nothing has
   * been chosen and when one or two parts have been, because no partial
   * date may ever be persisted. A host that ACCEPTS a blank date of birth
   * has to tell those apart, or a half-finished entry saves as though it
   * were deliberately left empty.
   *
   * Emitted on user interaction only, never from `writeValue` — emitting
   * mid-change-detection would trip ExpressionChangedAfterItHasBeenChecked
   * in a host that binds the flag. Hosts reset their own copy when they
   * reset the form; this component is inside an `*ngIf` in all three of
   * them, so it is recreated on every open and never has state to replay.
   */
  @Output() partialChange = new EventEmitter<boolean>();

  protected month = '';
  protected day = '';
  protected year = '';
  protected disabled = false;

  /** Last value handed to {@link partialChange}, so it only fires on change. */
  private partial = false;

  protected readonly months = [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
  ];

  /**
   * Newest first: a recent year is one click away and an older one is a
   * short type-ahead, which beats scrolling up from 1906.
   */
  protected readonly years: string[] = (() => {
    const current = new Date().getFullYear();
    const out: string[] = [];
    for (let y = current; y >= current - DATE_OF_BIRTH_PICKER_MAX_AGE_YEARS; y--) {
      out.push(String(y));
    }
    return out;
  })();

  private onChange: (value: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  /**
   * Days valid for the chosen month and year, so 31 February is never
   * offered and 29 February appears only in a leap year. Falls back to 31
   * before a month is picked, so the field is usable in any order.
   */
  protected get days(): string[] {
    const count = this.daysInMonth(Number(this.month), Number(this.year));
    const out: string[] = [];
    for (let d = 1; d <= count; d++) out.push(String(d).padStart(2, '0'));
    return out;
  }

  private daysInMonth(month: number, year: number): number {
    if (!month) return 31;
    // Day 0 of the NEXT month is the last day of this one. Built from
    // numeric parts in local time — no string is ever parsed as a date, so
    // no timezone can shift it.
    if (!year) return month === 2 ? 29 : new Date(2001, month, 0).getDate();
    return new Date(year, month, 0).getDate();
  }

  // --- ControlValueAccessor -------------------------------------------

  writeValue(value: string | null | undefined): void {
    const text = (value || '').trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
      this.year = text.slice(0, 4);
      this.month = text.slice(5, 7);
      this.day = text.slice(8, 10);
    } else {
      this.year = '';
      this.month = '';
      this.day = '';
    }
    // Kept in step without emitting — see `partialChange`. A written value
    // is always blank or complete, so this settles to false either way.
    this.partial = this.isPartial();
  }

  registerOnChange(fn: (value: string) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }
  setDisabledState(isDisabled: boolean): void { this.disabled = isDisabled; }

  // --- Interaction ----------------------------------------------------

  protected onPartChange(): void {
    // Changing month or year can strand a day that no longer exists —
    // picking 31 January then switching to February, say. Clamp it rather
    // than silently emitting an impossible date.
    if (this.day && this.month) {
      const max = this.daysInMonth(Number(this.month), Number(this.year));
      if (Number(this.day) > max) this.day = String(max).padStart(2, '0');
    }
    this.onChange(this.composed());
    // After the clamp, so the reported state describes what is actually
    // selected rather than what was picked.
    this.syncPartial();
  }

  /** Some parts chosen, but not all three. */
  private isPartial(): boolean {
    const chosen = [this.month, this.day, this.year].filter(Boolean).length;
    return chosen > 0 && chosen < 3;
  }

  private syncPartial(): void {
    const next = this.isPartial();
    if (next === this.partial) return;
    this.partial = next;
    this.partialChange.emit(next);
  }

  /**
   * Reports a touch only once focus has left the control ENTIRELY.
   *
   * Three selects are one field. Reporting a touch on each inner blur
   * would mark the control touched the moment the customer moved from
   * Month to Day — so a host that renders its "required" message on
   * `touched` would turn the field red mid-entry, while they were part
   * way through answering it.
   *
   * `focusout` rather than `blur` because only `focusout` bubbles, so one
   * listener on the wrapper covers all three selects. A null
   * `relatedTarget` (focus left the document — alt-tab, devtools) counts
   * as leaving: they are no longer answering the field either way.
   */
  protected onFocusOut(event: FocusEvent): void {
    const next = event.relatedTarget as Node | null;
    if (next && this.host.nativeElement.contains(next)) return;
    this.onTouched();
  }

  /**
   * `YYYY-MM-DD` once every part is chosen, otherwise empty so the host's
   * existing required-check still reports it as missing.
   */
  private composed(): string {
    if (!this.year || !this.month || !this.day) return '';
    return `${this.year}-${this.month}-${this.day}`;
  }
}
